import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { setRecoveryEmail } from "@/lib/passwordReset";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the saved email so the dashboard can update its host state. */
  onSaved: (email: string) => void;
}

/**
 * Nudge shown on the venue-admin dashboard when no recovery email is on file.
 * Dismissible ("Skip for now") but reappears on the next login until set — this
 * is how we collect recovery emails so password reset becomes usable.
 */
const RecoveryEmailModal = ({ open, onOpenChange, onSaved }: Props) => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast({ title: "Invalid email", description: "Enter a valid email address.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await setRecoveryEmail(trimmed);
      toast({ title: "Recovery email saved", description: "You can now reset your password by email if needed." });
      onSaved(trimmed);
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Couldn't save", description: err?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-console-surface border-console-line text-console-text">
        <DialogHeader>
          <DialogTitle className="font-geist tracking-tight flex items-center gap-2">
            <Mail className="h-4 w-4 text-console-primary" /> Add a recovery email
          </DialogTitle>
          <DialogDescription className="font-geist text-console-muted">
            Set a recovery email so you can reset your password if you ever get locked out.
            You can skip for now and add it later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="recovery-email" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Email</Label>
            <Input
              id="recovery-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@venue.com"
              className="h-11 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim rounded-[6px] focus:border-console-primary focus-visible:ring-0"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-11 text-[13px] font-geist text-console-muted hover:text-console-text border border-console-line rounded-[6px]"
            >
              Skip for now
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 h-11 text-[13px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
            >
              {saving ? "Saving…" : "Save email"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecoveryEmailModal;
